/**
 * 기본 API Composable
 * 모든 도메인 Composable에서 공통으로 사용하는 기능들
 */

import { apiClient } from "@/services/auth-header";
import cacheService from "@/services/common/cache.service.js";
import ApiErrorHandler from "@/services/common/api-error-handler.js";
import { readonly, ref } from "vue";

/**
 * @typedef {Object} ApiBaseOptions
 * @property {string} domain - 도메인 이름 (캐시 prefix용)
 * @property {number} [defaultTTL=300000] - 기본 캐시 TTL (5분)
 * @property {Object} [defaultParams={}] - 기본 파라미터
 */

/**
 * 기본 API 호출 기능을 제공하는 Composable
 * @param {ApiBaseOptions} options - 설정 옵션
 * @returns {Object} API 호출 관련 상태와 메서드들
 */
export function useApiBase(options = {}) {
  const {
    domain = "api",
    defaultTTL = 5 * 60 * 1000, // 5분
    defaultParams = {},
  } = options;

  // 기본 상태
  const loading = ref(false);
  const error = ref(null);

  /**
   * 캐시 키 생성 - 객체 속성 순서를 정렬하여 일관성 보장
   * @param {string} endpoint - API 엔드포인트
   * @param {Object} params - 파라미터
   * @returns {string}
   */
  const getCacheKey = (endpoint, params) => {
    if (!params || Object.keys(params).length === 0) {
      return cacheService.createKey(domain, endpoint);
    }

    // 객체의 키를 알파벳순으로 정렬
    const sortedKeys = Object.keys(params).sort();
    const sortedParams = sortedKeys.reduce((acc, key) => {
      acc[key] = params[key];
      return acc;
    }, {});

    const key = `${endpoint}_${JSON.stringify(sortedParams)}`;
    return cacheService.createKey(domain, key);
  };

  /**
   * V2 API 응답 데이터 추출
   * @param {Object} response - Axios 응답 객체
   * @returns {*} 추출된 데이터
   */
  const extractV2ResponseData = (response) => {
    // 1. HTTP Status Code 확인 (가장 중요)
    if (response.status < 200 || response.status >= 300) {
      throw new Error(`HTTP Error: ${response.status} ${response.statusText}`);
    }

    // 2. Application-level 응답이 없는 경우 (단순 HTTP 성공)
    if (!response.data) {
      return null;
    }

    // 3. Application-level 에러 코드 확인 (HTTP 200 내에서)
    if (response.data.code) {
      const isApplicationSuccess =
        response.data.code === "SUCCESS" || response.data.code === 200;

      if (!isApplicationSuccess) {
        throw new Error(
          response.data.message || `Application Error: ${response.data.code}`
        );
      }
    }

    // 4. V2 API 응답 구조에서 데이터 추출: { code, message, data: {...} }
    if (response.data?.data) {
      return response.data.data;
    }

    // 5. Fallback: 직접 응답 데이터 반환
    return response.data;
  };

  /**
   * 기본 파라미터 검증
   * @param {Object} params
   */
  const validatePaginationParams = (params) => {
    if (
      params.page !== undefined &&
      (typeof params.page !== "number" || params.page < 0)
    ) {
      throw new Error("페이지 번호는 0 이상의 숫자여야 합니다");
    }

    if (
      params.size !== undefined &&
      (typeof params.size !== "number" || params.size <= 0 || params.size > 100)
    ) {
      throw new Error("페이지 크기는 1-100 사이의 숫자여야 합니다");
    }

    if (
      params.direction !== undefined &&
      !["asc", "desc", "ASC", "DESC"].includes(params.direction)
    ) {
      throw new Error("정렬 방향은 asc 또는 desc여야 합니다");
    }
  };

  /**
   * API 호출 실행
   * @param {string} endpoint - API 엔드포인트
   * @param {Object} params - 요청 파라미터
   * @param {Object} options - 요청 옵션
   * @returns {Promise<*>} API 응답 데이터
   */
  const executeApiCall = async (endpoint, params = {}, options = {}) => {
    const {
      method = "GET",
      data = null,
      cache = true,
      cacheTTL = defaultTTL,
      validateParams = null,
    } = options;

    try {
      // 파라미터 검증
      if (validateParams) {
        validateParams(params);
      }

      const finalParams = { ...defaultParams, ...params };

      // 캐시 확인 (GET 요청이고 캐시가 활성화된 경우)
      if (method === "GET" && cache) {
        const cacheKey = getCacheKey(endpoint, finalParams);
        const cachedData = cacheService.get(cacheKey);
        if (cachedData) {
          return cachedData;
        }
      }

      loading.value = true;
      error.value = null;

      // API 호출
      let response;
      const config = method === "GET" ? { params: finalParams } : data;

      switch (method.toUpperCase()) {
        case "GET":
          response = await apiClient.get(endpoint, config);
          break;
        case "POST":
          response = await apiClient.post(endpoint, config);
          break;
        case "PUT":
          response = await apiClient.put(endpoint, config);
          break;
        case "DELETE":
          response = await apiClient.delete(endpoint, config);
          break;
        default:
          throw new Error(`지원하지 않는 HTTP 메서드: ${method}`);
      }

      const responseData = extractV2ResponseData(response);

      // 성공적인 GET 응답 캐시
      if (method === "GET" && cache) {
        const cacheKey = getCacheKey(endpoint, finalParams);
        cacheService.set(cacheKey, responseData, cacheTTL);
      }

      return responseData;
    } catch (err) {
      // 공통 에러 핸들러 사용
      const handledError = ApiErrorHandler.handleError(err, {
        service: `use${domain.charAt(0).toUpperCase() + domain.slice(1)}`,
        method: "executeApiCall",
        params: { endpoint, params },
      });

      error.value = handledError;
      throw handledError;
    } finally {
      loading.value = false;
    }
  };

  /**
   * 무한스크롤을 위한 배열 데이터 관리
   * @param {import('vue').Ref} dataRef - 데이터 배열 ref
   * @param {*} newData - 새로운 데이터
   * @param {boolean} append - 추가 모드 여부
   * @param {string} [contentKey='content'] - 컨텐츠 배열 키
   */
  const updateArrayData = (
    dataRef,
    newData,
    append = false,
    contentKey = "content"
  ) => {
    const content = newData[contentKey] || newData;

    if (append && Array.isArray(content)) {
      dataRef.value.push(...content);
    } else if (Array.isArray(content)) {
      dataRef.value = content;
    } else {
      dataRef.value = [];
    }
  };

  /**
   * 도메인별 캐시 초기화
   */
  const clearCache = () => {
    cacheService.deleteByPrefix(domain);
  };

  /**
   * 상태 초기화
   */
  const reset = () => {
    loading.value = false;
    error.value = null;
  };

  return {
    // 읽기 전용 상태
    loading: readonly(loading),
    error: readonly(error),

    // 유틸리티 메서드
    executeApiCall,
    updateArrayData,
    validatePaginationParams,
    getCacheKey,
    clearCache,
    reset,
  };
}
